import React, { useEffect, useMemo, useState } from 'react'
import { useUserCategories, useProfile, useCourseList, useGetQuery } from '../../hooks'
import HomeLayout from '../layouts/homeCourseList'
import { CourseTab, ScrollArrow } from './components'
import { Container, SiderBar, Image, Sidebar, SiderBarToggle } from '../../components'
import Wrapper from './styled'
import { TabsName, USER_ROLE } from '../../constants'
import { SIDEBAR_ICON } from '../../assets'

const CourseListScreen = () => {
  // Use hooks
  const { profile, profile: { isWorkSpace }, userRole } = useProfile()
  const { fromTab, workspaceid } = useGetQuery()
  const { userCategories, companyCategories, nissokenCategories } = useUserCategories({ userId: profile?.userId, workspaceid })
  const {
    getCourseNissoken,
    getCourseNissokenNew,
    getCourseNissokenRecommended,
    getCourseNissokenRecentViews,
    getCourseCompany,
    getCourseCompanyNew,
    getCourseCompanyRecommended,
    getCourseCompanyRecentViews,
    getChannelCounts,
    savedFilter,
    channelCounts,
    courseCompany,
    courseCompanyNew,
    courseCompanyRecommended,
    courseCompanyRecentViews,
    courseNissoken,
    courseNissokenNew,
    courseNissokenRecommended,
    courseNissokenRecentViews,
    viewsCourseList,
    courseListTab,
    isCourseListPage,
    isSearchResultPage,
    updateViewedTabAction,
    needsToLoadCompanyCategories,
    setNeedsToLoadCompanyCategories,
    needsToLoadNissokenCategories,
    setNeedsToLoadNissokenCategories,
    needsToLoadUncategorizedCompany,
    setNeedsToLoadUncategorizedCompany,
    needsToLoadUncategorizedNissoken,
    setNeedsToLoadUncategorizedNissoken
  } = useCourseList({ userId: profile.userId })

  // Use states
  const [categories, setCategories] = useState([])
  const [courseSearch, setCourseSearch] = useState('')
  const [isSidebar, setIsSidebar] = useState(false)
  const [checkedKeys, setCheckedKeys] = useState({ company: [], nissoken: [] })
  const [isSidebarToggle, setIsSidebarToggle] = useState(true)
  // End use states

  const isUserCompany = useMemo(() => [USER_ROLE.COMPANY_EMPLOYEE, USER_ROLE.COMPANY_ADMIN, USER_ROLE.SUB_ADMINISTRATOR, USER_ROLE.COURSE_ADMIN].includes(userRole), [userRole])
  const isUserNissoken = useMemo(() => userRole === USER_ROLE.NISSHOKEN_ADMIN, [userRole])

  useEffect(() => {
    setCategories(savedFilter?.categories || [])
    setCourseSearch(savedFilter?.courseSearch || '')
  }, [savedFilter])

  useEffect(() => {
    getChannelCounts(savedFilter)
  }, [workspaceid, savedFilter])

  useEffect(() => {
    const updated = nissokenCategories.map(({ key }) => ({ key, load: true }))
    setNeedsToLoadNissokenCategories(updated)
  }, [nissokenCategories])

  useEffect(() => {
    const updated = companyCategories.map(({ key }) => ({ key, load: true }))
    setNeedsToLoadCompanyCategories(updated)
  }, [companyCategories])

  const getCourseByTab = ({ tab }) => {
    if (tab === TabsName.COMPANY_COURSE) {
      if (isSearchResultPage) {
        getCourseCompany({ ...savedFilter, page: courseCompany.page, limit: courseCompany.limit, initial: true })
      }

      if (isCourseListPage && !isWorkSpace) {
        getCourseCompanyNew({ ...savedFilter, page: courseCompanyNew.page, limit: courseCompanyNew.limit, initial: true })
        getCourseCompanyRecommended({ ...savedFilter, page: courseCompanyRecommended.page, limit: courseCompanyRecommended.limit, initial: true })
        getCourseCompanyRecentViews({ ...savedFilter, page: courseCompanyRecentViews.page, limit: courseCompanyRecentViews.limit, initial: true })
      }

      if (isCourseListPage) {
        setNeedsToLoadCompanyCategories((prev) => prev.map((item) => ({ ...item, load: true })))
        setNeedsToLoadUncategorizedCompany(true)
      }
    } else if (tab === TabsName.NISSOKEN_COURSE) {
      if (isSearchResultPage) {
        getCourseNissoken({ ...savedFilter, page: courseNissoken.page, limit: courseNissoken.limit, initial: true })
      }

      if (isCourseListPage && !isWorkSpace) {
        getCourseNissokenNew({ ...savedFilter, page: courseNissokenNew.page, limit: courseNissokenNew.limit, initial: true })
        getCourseNissokenRecommended({ ...savedFilter, page: courseNissokenRecommended.page, limit: courseNissokenRecommended.limit, initial: true })
        getCourseNissokenRecentViews({ ...savedFilter, page: courseNissokenRecentViews.page, limit: courseNissokenRecentViews.limit, initial: true })
      }

      if (isCourseListPage) {
        setNeedsToLoadNissokenCategories((prev) => prev.map((item) => ({ ...item, load: true })))
        setNeedsToLoadUncategorizedNissoken(true)
      }
    }
  }

  useEffect(() => {
    if (profile.userId && [USER_ROLE.COMPANY_EMPLOYEE, USER_ROLE.COMPANY_ADMIN, USER_ROLE.SUB_ADMINISTRATOR, USER_ROLE.COURSE_ADMIN, USER_ROLE.NISSHOKEN_ADMIN].includes(userRole)) {
      getCourseByTab({ tab: fromTab })
      updateViewedTabAction({ tab: fromTab })
    }
  }, [profile.userId, workspaceid, viewsCourseList])

  return (
    <HomeLayout
      isUserCompany={isUserCompany}
      isUserNissoken={isUserNissoken}
      setCourseSearch={setCourseSearch}
      courseSearch={courseSearch}
      setCategories={setCategories}
      checkedKeys={checkedKeys}
      setCheckedKeys={setCheckedKeys}
    >
      <Wrapper tab={courseListTab}>
        <Container className={isSidebarToggle ? '' : 'sidebar--close'}>
          <Image className="sidebar__icon" onClick={() => setIsSidebar(true)} src={SIDEBAR_ICON} />

          {/* 狭い画面時のサイドバー */}
          <Sidebar width={390} placement="left" visible={isSidebar} onVisible={setIsSidebar}>
            <SiderBarToggle
              userCategories={userCategories}
              categories={categories}
              companyCategories={companyCategories}
              nissokenCategories={nissokenCategories}
              isUserCompany={isUserCompany}
              isUserNissoken={isUserNissoken}
              setCategories={setCategories}
              setCourseSearch={setCourseSearch}
              courseSearch={courseSearch}
              profile={profile}
              userRole={userRole}
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
            />
          </Sidebar>

          {/* 広い画面時のサイドバー */}
          <SiderBar
            userCategories={userCategories}
            categories={categories}
            companyCategories={companyCategories}
            nissokenCategories={nissokenCategories}
            isUserCompany={isUserCompany}
            isUserNissoken={isUserNissoken}
            setCategories={setCategories}
            setCourseSearch={setCourseSearch}
            courseSearch={courseSearch}
            profile={profile}
            userRole={userRole}
            checkedKeys={checkedKeys}
            setCheckedKeys={setCheckedKeys}
            isSidebarToggle={isSidebarToggle}
            setIsSidebarToggle={setIsSidebarToggle}
          />

          <div className="top">
            <div className="course-content">
              <div className="course-type">
                <CourseTab
                  categories={categories}
                  companyCategories={companyCategories}
                  nissokenCategories={nissokenCategories}
                  courseSearch={savedFilter.courseSearch}
                  channelCounts={channelCounts}
                  fromTab={fromTab}
                  getCourseByTab={getCourseByTab}
                  needsToLoadCompanyCategories={needsToLoadCompanyCategories}
                  setNeedsToLoadCompanyCategories={setNeedsToLoadCompanyCategories}
                  needsToLoadNissokenCategories={needsToLoadNissokenCategories}
                  setNeedsToLoadNissokenCategories={setNeedsToLoadNissokenCategories}
                  needsToLoadUncategorizedCompany={needsToLoadUncategorizedCompany}
                  setNeedsToLoadUncategorizedCompany={setNeedsToLoadUncategorizedCompany}
                  needsToLoadUncategorizedNissoken={needsToLoadUncategorizedNissoken}
                  setNeedsToLoadUncategorizedNissoken={setNeedsToLoadUncategorizedNissoken}
                  isSidebarToggle={isSidebarToggle}
                />
              </div>
            </div>
          </div>
          <ScrollArrow />
        </Container>
      </Wrapper>
    </HomeLayout>
  )
}

export default CourseListScreen
